import React, { useState, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useLazyCommand, useDomainEvent } from '@soap/modules';

interface DeleteUnitDialogProps {
    unitId: string;
    unitTitle: string;
    actionName?: string; // Optional: "Delete" (default) or "Abandon"
    onConfirm: () => void;
    onClose: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function DeleteUnitDialog({ unitId, unitTitle, actionName = "Delete", onConfirm, onClose }: DeleteUnitDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendDeleteUnitCommand] = useLazyCommand('C265v1_DeleteUnit');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: "close-context-menu",
    });

    const handleClose = useCallback(() => {
        setViewState(viewStates.ready);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleDelete = async () => {
        setViewState(viewStates.submitting);
        
        // Notify parent to set loading state
        onConfirm();

        try {
            await sendDeleteUnitCommand({
                C265_UnitId: unitId
            });
            handleClose();
        } catch (error) {
            console.error('Error deleting unit:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">{actionName} Unit</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
                            <p className="text-sm text-destructive-action-dark">
                                This action cannot be undone.
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                Are you sure you want to {actionName.toLowerCase()} the unit <strong>{unitTitle}</strong>?
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                This will permanently delete the unit and all its lessons and activities.
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                All assessments associated with this unit and any assessment results will also be permanently deleted.
                            </p>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                                <Trash2 size={16} className="mr-2" />
                                What will be deleted:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-yellow-800">The entire unit</li>
                                <li className="text-sm text-yellow-800">All lessons in this unit</li>
                                <li className="text-sm text-yellow-800">All activities associated with those lessons</li>
                                <li className="text-sm text-yellow-800">All assessments associated with this unit</li>
                                <li className="text-sm text-yellow-800">All assessment results</li>
                                <li className="text-sm text-yellow-800">All progress and data related to this unit</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <Trash2 size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? `${actionName}ing...` : `${actionName} Unit`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

